import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// Components
import ConnectionsMobileSubnav from './ConnectionsMobileSubnav';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import SectionHeader from '../../../_molecules/SectionHeader';
import ErrorHandler from '../../../_molecules/ErrorHandler';
import TrackPageView from '@tracking/TrackPageView';
import SearchSavedContacts from '../../../searches/SearchSavedContacts';
import LinkWithHistory from '@atoms/LinkWithHistory';
import EmptyStateContainer from '@molecules/EmptyStateContainer';
import LoadingArea from '@molecules/LoadingArea';
import { BodyLarge, DivWithShadowAlternate, H3, LinkSmall, SectionWrapperInner, SectionWrapperOuter } from '@styled';

// Functionality
import useSavedContactsActive from '@hooks/useSavedContactsActive';
import useAlgoliaMembers from '@hooks/search/useAlgoliaMembers';
import useDocumentTitle from '@hooks/useDocumentTitle';

const SearchContainerStyled = styled(DivWithShadowAlternate)`
    margin-top: 40px;
    padding: 32px;
`;

const NoResultsBlock = () => (
    <span>
        Create a{' '}
        <LinkWithHistory className="text-color-link" href="/my-icsc/connections/matches/new">
            match criteria
        </LinkWithHistory>{' '}
        and click on the star icon for any member to add them to this Saved Contacts page.
    </span>
);

const ContactsSavedPage = () => {
    const [savedContactIds, setSavedContactIds] = useState();
    const { data: savedContacts, loading } = useSavedContactsActive();
    useDocumentTitle('Saved Contacts');

    // This is necessary because saved records are not always in sync with what's in Algolia,
    // leading to an empty Algolia search instead of the Empty State Container we want to show.
    const { members: algoliaMembers } = useAlgoliaMembers(savedContactIds);

    useEffect(() => {
        if (!savedContacts) {
            return;
        }

        setSavedContactIds(savedContacts.map(c => c.recordId));
    }, [savedContacts]);

    return (
        <ErrorHandler>
            <TrackPageView>
                <ConnectionsMobileSubnav />

                <SectionWrapperOuter>
                    <SectionWrapperInner>
                        <div className="fade-in-fast pb-4">
                            <SectionHeader title="My ICSC Connections" />

                            <div className="flex justify-content-between mb-1.5rem">
                                <SectionBreadcrumbs className="mt-2">
                                    <SectionBreadcrumb route="/my-icsc/connections">Connections</SectionBreadcrumb>
                                    <SectionBreadcrumb isActive>Saved Contacts</SectionBreadcrumb>
                                </SectionBreadcrumbs>
                            </div>

                            <H3 className="mt-2 mb-3">Saved Contacts</H3>

                            <BodyLarge>
                                These are the ICSC members that you’ve saved. Start a conversation, schedule a meeting,
                                or view available listings from members you’ve already connected with.
                            </BodyLarge>

                            <div className="mt-4">
                                {loading && <LoadingArea />}

                                {algoliaMembers?.length === 0 && !loading && (
                                    <EmptyStateContainer
                                        Body={NoResultsBlock}
                                        header="You haven't saved any contacts yet."
                                        imageSrc="/assets/modules/images/SavedContactsEmptyStatePlaceholder.jpg"
                                        imageAlt="No saved contacts"
                                        ctaText="View My Matches"
                                        ctaHref="/my-icsc/connections/matches"
                                        onCTAClick={() => {}}
                                    />
                                )}

                                {algoliaMembers?.length > 0 && !loading && (
                                    <SearchContainerStyled>
                                        <SearchSavedContacts
                                            defaultViewType="list"
                                            parentView="Connections"
                                            showPagingToolbar
                                            showSearchBox
                                            showSearchOptions
                                            updateSearchStateUrl
                                            NoResultsText={NoResultsBlock}
                                        />
                                    </SearchContainerStyled>
                                )}
                            </div>
                        </div>
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

export default ContactsSavedPage;
